import {
  Directive,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/core/app.state';
import { selectCanAccessModule, selectCanDo, selectHasPermission } from 'src/app/core/store/permissions/permissions.selectors';

export interface HasPermissionContext {
  module?: string;
  action?: string;
  permission?: string;
}

@Directive({
  selector: '[appHasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly requirement$ = new Subject<string | HasPermissionContext>();
  private hasView = false;

  constructor(
    private readonly templateRef: TemplateRef<unknown>,
    private readonly viewContainer: ViewContainerRef,
    private readonly store: Store<AppState>
  ) {
    this.requirement$
      .pipe(
        distinctUntilChanged((previous, current) => {
          if (previous === current) {
            return true;
          }
          if (typeof previous === 'string' || typeof current === 'string') {
            return previous === current;
          }
          return (
            previous.module === current.module &&
            previous.action === current.action &&
            previous.permission === current.permission
          );
        }),
        switchMap((requirement) => this.resolveSelector(requirement)),
        takeUntil(this.destroy$)
      )
      .subscribe((hasAccess) => this.updateView(hasAccess));
  }

  @Input()
  set appHasPermission(requirement: string | HasPermissionContext) {
    this.requirement$.next(requirement);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resolveSelector(requirement: string | HasPermissionContext) {
    if (typeof requirement === 'string') {
      return this.store.select(selectHasPermission(requirement));
    }

    if (requirement.permission) {
      return this.store.select(selectHasPermission(requirement.permission));
    }

    if (!requirement.module) {
      return this.store.select(() => true);
    }

    return this.store.select(
      requirement.action
        ? selectCanDo(requirement.module, requirement.action)
        : selectCanAccessModule(requirement.module)
    );
  }

  private updateView(hasAccess: boolean): void {
    if (hasAccess && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
      return;
    }

    if (!hasAccess && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}
